/* eslint-disable @typescript-eslint/no-explicit-any */
import axiosInstance from './axioInstance';
import { useQuery } from "@tanstack/react-query";
import analyticsMockData from "./analyticsMock.json";

export const PATHS = Object.freeze({
  GET_ANALYTICS_INTERACTION: "/analytics?analytic_type=interaction",
  GET_ANALYTICS_CONNECTION: "/analytics?analytic_type=connection",
  GET_TEAM_ANALYTICS_INTERACTION: "/analytics/team?analytic_type=interaction",
  GET_TEAM_ANALYTICS_CONNECTION: "/analytics/team?analytic_type=connection",
  GET_TEAM_ANALYTICS_EXECUTION_SCORE: "/analytics/actionitem/team?by_assignor=true",

  GET_TEAM_INFO: "/me/team",

  GET_INSIGHTS_MEETINGS: "/me/team/chart/meetings",
  GET_INSIGHTS_ACTION_ITEMS: "/me/team/chart/action-items",
  GET_INSIGHTS_ACCOUNTS: "/me/team/chart/accounts",

  GET_ACCOUNT_INSIGHTS: "/me/team/accounts",
  GET_CLIENT_REPORT: "/accounts/client-report",
  GET_DOWNLOAD_URL: "/generate/download-url",
  GET_TEAM_INSIGHTS: "/me/team/meetings",

  GET_USER_MEETINGS_ATTENDED: "/me/team/meetings/list",
  GET_QUICK_SUMMARY: "/client/quick-summary"
});

interface ClientReportParams {
  view_files?: boolean,
  download_url?: string
}

const getMeetingCategories = async (): Promise<any> => {
  return new Promise((resolve) => {
    resolve({ data: analyticsMockData.meetingsCategory })
  })
};

const getAnalyticsTimeInMeetings = async (start_time: string, end_time: string, calendarId: string): Promise<any> => {
  const response = await axiosInstance.get(PATHS.GET_ANALYTICS_INTERACTION, {
    params: {
      start_time: start_time,
      end_time: end_time,
      cal_ids: calendarId,
    }
  });
  return response;
};

const useAnalyticsTimeInMeetings = (params: any, enabled: boolean) => {
  return useQuery<string>({
    queryKey: ['get_analytics-time_in_meetings', `${params.calendarId}_${params.start_time}_${params.end_time}`],
    queryFn: () => getAnalyticsTimeInMeetings(params.start_time, params.end_time, params.calendarId),
    enabled: enabled
  })
}

const getInternalvsExternal = async (start_time: string, end_time: string, calendarId: string): Promise<any> => {
  const response = await axiosInstance.get(PATHS.GET_ANALYTICS_CONNECTION, {
    params: {
      start_time: start_time,
      end_time: end_time,
      cal_ids: calendarId,
    }
  });
  return response;
};

const useInternalvsExternal = (params: any, enabled: boolean) => {
  return useQuery<string>({
    queryKey: ['get_internal_vs_external', `${params.calendarId}_${params.start_time}_${params.end_time}`],
    queryFn: () => getInternalvsExternal(params.start_time, params.end_time, params.calendarId),
    enabled: enabled
  })
}

const getEffectivevsIneffective = async (): Promise<any> => {
  return new Promise((resolve) => {
    resolve({ data: analyticsMockData.effectiveIneffectiveMeetings })
  })
};

const getTeamAnalyticsTimeInMeetings = async (start_time: string, end_time: string, email_ids: string): Promise<any> => {
  const response = await axiosInstance.get(PATHS.GET_TEAM_ANALYTICS_INTERACTION, {
    params: {
      start_time: start_time,
      end_time: end_time,
      email_ids: email_ids,
    }
  });
  return response;
};

const useTeamAnalyticsTimeInMeetings = (params: any, enabled: boolean) => {
  return useQuery<string>({
    queryKey: ['get_team_analytics-time_in_meetings', `${params.email_ids}_${params.start_time}_${params.end_time}`],
    queryFn: () => getTeamAnalyticsTimeInMeetings(params.start_time, params.end_time, params.email_ids),
    enabled: enabled
  })
}

const getTeamInternalvsExternal = async (start_time: string, end_time: string, email_ids: string): Promise<any> => {
  const response = await axiosInstance.get(PATHS.GET_TEAM_ANALYTICS_CONNECTION, {
    params: {
      start_time: start_time,
      end_time: end_time,
      email_ids: email_ids,
    }
  });
  return response;
};

const useTeamInternalvsExternal = (params: any, enabled: boolean) => {
  return useQuery<string>({
    queryKey: ['get_team_internal_vs_external', `${params.email_ids}_${params.start_time}_${params.end_time}`],
    queryFn: () => getTeamInternalvsExternal(params.start_time, params.end_time, params.email_ids),
    enabled: enabled
  })
}

const getTeamAnalyticsExecutionScore = async (start_time: string, end_time: string, email_ids: string): Promise<any> => {
  const response = await axiosInstance.get(PATHS.GET_TEAM_ANALYTICS_EXECUTION_SCORE, {
    params: {
      start_time: start_time,
      end_time: end_time,
      email_ids: email_ids,
    }
  });
  return response;
};

const useTeamAnalyticsExecutionScore = (params: any, enabled: boolean) => {
  return useQuery<string>({
    queryKey: ['get_team_analytics-execution_score', `${params.email_ids}_${params.start_time}_${params.end_time}`],
    queryFn: () => getTeamAnalyticsExecutionScore(params.start_time, params.end_time, params.email_ids),
    enabled: enabled
  })
}

const getTeamInfo = async (employee_email_ids?: string, depth_level?: number, impersonate_email?:string): Promise<any> => {
  const response = await axiosInstance.get(PATHS.GET_TEAM_INFO, {
    params: { employee_email_ids, depth_level, impersonate_email }
 });
  return response;
};

const useGetTeamInfo = (enabled: boolean, employee_email_ids?: string | undefined, depth_level?: number, impersonate_email?: string) => {
  return useQuery<string>({
    queryKey: ['get_team_info', employee_email_ids, depth_level, impersonate_email],
    queryFn: () => getTeamInfo(employee_email_ids!, depth_level!, impersonate_email),
    enabled: enabled || !!employee_email_ids
  })
}


interface InsightDistributionParams {
  start_time: string;
  end_time: string;
  employee_email_ids?: string;
  include_me?: boolean;
  impersonate_email?: string;
}

const getInsightsMeeting = async (params: InsightDistributionParams): Promise<any> => {
  const response = await axiosInstance.get(PATHS.GET_INSIGHTS_MEETINGS, {
    params: {
      start_time: params.start_time,
      end_time: params.end_time,
      employee_email_ids: params.employee_email_ids,
      include_me: params.include_me,
      impersonate_email: params.impersonate_email,
    }
  });
  return response;
}

const useGetInsightsMeetings = (params: InsightDistributionParams, enabled: boolean) => {
  return useQuery<string>({
    queryKey: ['get_insights_meetings', params],
    queryFn: () => getInsightsMeeting(params),
    enabled: enabled
  })
}

const getInsightsActionItems = async (params: InsightDistributionParams): Promise<any> => {
  const response = await axiosInstance.get(PATHS.GET_INSIGHTS_ACTION_ITEMS, {
    params: {
      start_time: params.start_time,
      end_time: params.end_time,
      employee_email_ids: params.employee_email_ids,
      include_me: params.include_me,
      impersonate_email: params.impersonate_email,
    }
  });
  return response;
}

const useGetInsightsActionItems = (params: InsightDistributionParams, enabled: boolean) => {
  return useQuery<string>({
    queryKey: ['get_insights_action_items', params],
    queryFn: () => getInsightsActionItems(params),
    enabled: enabled
  })
}

const getInsightsAccounts = async (params: InsightDistributionParams): Promise<any> => {
  const response = await axiosInstance.get(PATHS.GET_INSIGHTS_ACCOUNTS, {
    params: {
      start_time: params.start_time,
      end_time: params.end_time,
      employee_email_ids: params.employee_email_ids,
      include_me: params.include_me,
      impersonate_email: params.impersonate_email,
    }
  });
  return response;
}

const useGetInsightsAccounts = (params: InsightDistributionParams, enabled: boolean) => {
  return useQuery<string>({
    queryKey: ['get_insights_accounts', params],
    queryFn: () => getInsightsAccounts(params),
    enabled: enabled
  })
}

interface GetInsightsParams {
  start_time: string;
  end_time: string;
  employee_email_ids?: string
  include_me?: boolean,
  nps_category?: string
  pageSize?: number;
  impersonate_email?:string;
  sort?: { sort_field: string, sort_order: string },
  page?: number;
  is_rated?: boolean;
}

const getAccountInsights = async (params: GetInsightsParams): Promise<any> => {
  const response = await axiosInstance.get(PATHS.GET_ACCOUNT_INSIGHTS, {
    params: {
      start_time: params.start_time,
      end_time: params.end_time,
      employee_email_ids: params.employee_email_ids,
      include_me: params.include_me,
      nps_category: params.nps_category,
      per_page: params.pageSize,
      impersonate_email: params.impersonate_email,
      sort_field : params.sort?.sort_field,
      sort_order: params.sort?.sort_order,
      page: params.page,
      is_rated: params.is_rated
    }
  });
  return response;
}
const getClientReport = async (params: ClientReportParams): Promise<any> => {
  const response = await axiosInstance.get(PATHS.GET_CLIENT_REPORT, {
    params: {
      view_files: params.view_files,
    }
  });
  return response;
}
const getDownloadUrl = async (params: ClientReportParams): Promise<any> => {
  const response = await axiosInstance.get(PATHS.GET_DOWNLOAD_URL, {
    params: {
      download_url: params.download_url
    }
  });
  return response;
}

const useGetAccountInsights = (params: GetInsightsParams, enabled: boolean) => {
  return useQuery<string>({
    queryKey: ['get_account_insights', params],
    queryFn: () => getAccountInsights(params),
    enabled: enabled
  })
}

const useGetClientReport = (params: ClientReportParams, enabled: boolean) => {
  return useQuery<string>({
    queryKey: ['get_client_report', params],
    queryFn: () => getClientReport(params),
    enabled: enabled
  })
}
const useDownloadUrl = (params: ClientReportParams, enabled: boolean) => {
  return useQuery<string>({
    queryKey: ['get_download_url', params],
    queryFn: () => getDownloadUrl(params),
    enabled: enabled
  })
}

const getTeamInsights = async (params: GetInsightsParams): Promise<any> => {
  const response = await axiosInstance.get(PATHS.GET_TEAM_INSIGHTS, {
    params: {
      start_time: params.start_time,
      end_time: params.end_time,
      employee_email_ids: params.employee_email_ids,
      include_me: params.include_me,
      impersonate_email: params.impersonate_email,
      sort_field : params.sort?.sort_field,
      sort_order: params.sort?.sort_order,
      per_page: params?.pageSize,
      page: params?.page,
    }
  });
  return response;
}

const useGetTeamInsights = (params: GetInsightsParams, enabled: boolean) => {
  return useQuery<string>({
    queryKey: ['get_team_insights', params],
    queryFn: () => getTeamInsights(params),
    enabled: enabled
  })
}

interface UserMeetingsAttendedParams {
  cal_ids?: string;
  start_time: string;
  end_time: string;
  search?: string;
  is_external?: boolean | undefined;
  page: number;
  per_page: number,
  employee_email_ids?: string;
  group_type?: string
  sort?: { sort_field: string, sort_order: string }
  include_me?: boolean
  meeting_title?:string;
  overall_sentiment?:string;
  client_domain?: string;
  impersonate_email?: string;
  depth_level?: number;
}
const getUserMeetingsAttended = async (params: UserMeetingsAttendedParams): Promise<any> => {
  const response = await axiosInstance.get(PATHS.GET_USER_MEETINGS_ATTENDED, {
    params: {
      start_time: params.start_time,
      end_time: params.end_time,
      page: params.page,
      per_page: params.per_page,
      ...(params.search && { search: params.search }), // Add only if defined
      ...(params.sort && { sort_field: params.sort.sort_field, sort_order: params.sort.sort_order }), // Add only if defined
      ...(params.client_domain && { client_domain: params.client_domain }), // Add only if defined
      ...(params.employee_email_ids && { employee_email_ids: params.employee_email_ids }), // Add only if defined
      ...(params.is_external !== undefined && { is_external: params.is_external }),
      ...(params.include_me !== undefined && { include_me: params.include_me }), // Add only if not undefined
      ...(params.meeting_title && { meeting_title: params.meeting_title }),
      ...(params.overall_sentiment && { overall_sentiment: params.overall_sentiment }),
      ...(params.impersonate_email && { impersonate_email: params.impersonate_email }),
      ...(params.depth_level !== undefined && { depth_level: params.depth_level }),
    }
  });
  return response;
}

const useGetUserMeetingsAttended = (params: UserMeetingsAttendedParams, enabled: boolean) => {
  return useQuery<string>({
    queryKey: ['get_user_meeting_attended', params],
    queryFn: () => getUserMeetingsAttended(params),
    enabled: enabled
  })
}

const getQuickSummary = async (start_time: string, end_time: string, client_domain: string, include_me: boolean, impersonate_email?:string): Promise<any> => {
  const response = await axiosInstance.get(PATHS.GET_QUICK_SUMMARY, {
    params: {
      start_time: start_time,
      end_time: end_time,
      client_domain: client_domain,
      include_me: include_me,
      impersonate_email: impersonate_email,
    }
  });
  return response.data;
};

const useQuickSummary = (params: { start_time: string; end_time: string; client_domain: string; include_me: boolean; impersonate_email?:string; }, enabled: boolean) => {
  return useQuery<any>({
    queryKey: [
      'get_quick_summary',
      `${params.client_domain}_${params.start_time}_${params.end_time}_${params.include_me}_${params.impersonate_email}`,
    ],
    queryFn: () =>
      getQuickSummary(params.start_time, params.end_time, params.client_domain, params.include_me, params.impersonate_email),
    enabled: enabled,
  });
};

export interface AnalyticsAPIClient {
  readonly getMeetingCategories: () => Promise<any>;
  readonly useAnalyticsTimeInMeetings: (params: any, enabled: boolean) => any;
  readonly useInternalvsExternal: (params: any, enabled: boolean) => any;
  readonly getEffectivevsIneffective: () => Promise<any>;
  readonly useTeamAnalyticsTimeInMeetings: (params: any, enabled: boolean) => any;
  readonly useTeamInternalvsExternal: (params: any, enabled: boolean) => any;
  readonly useTeamAnalyticsExecutionScore: (params: any, enabled: boolean) => any;
  readonly useGetTeamInfo: (enabled: boolean, email?: string | undefined, depth_level?:number, impersonate_email?: string) => any;
  readonly useGetInsightsMeetings: (params: InsightDistributionParams, enabled: boolean) => any;
  readonly useGetInsightsActionItems: (params: InsightDistributionParams, enabled: boolean) => any;
  readonly useGetInsightsAccounts: (params: InsightDistributionParams, enabled: boolean) => any;
  readonly useGetTeamInsights: (params: GetInsightsParams, enabled: boolean) => any;
  readonly useGetAccountInsights: (params: GetInsightsParams, enabled: boolean) => any;
  readonly useDownloadUrl: (params: ClientReportParams, enabled: boolean) => any;
  readonly useGetClientReport: (params: ClientReportParams, enabled: boolean) => any;
  readonly useGetUserMeetingsAttended: (params: UserMeetingsAttendedParams, enabled: boolean) => any;
  readonly useQuickSummary: (params: { start_time: string; end_time: string; client_domain: string; include_me: boolean, impersonate_email?: string }, enabled: boolean) => any;
}

const AnalyticsAPIClient: AnalyticsAPIClient = Object.freeze({
  getMeetingCategories,
  useAnalyticsTimeInMeetings,
  useInternalvsExternal,
  getEffectivevsIneffective,
  useTeamAnalyticsTimeInMeetings,
  useTeamInternalvsExternal,
  useTeamAnalyticsExecutionScore,
  useGetTeamInfo,
  useGetInsightsMeetings,
  useGetInsightsActionItems,
  useGetInsightsAccounts,
  useGetAccountInsights,
  useGetClientReport,
  useDownloadUrl,
  useGetTeamInsights,
  useGetUserMeetingsAttended,
  useQuickSummary
});

export default AnalyticsAPIClient;